import { UntitledIcon } from '@faceup/icons'
import { ulPlus } from '@faceup/icons/ulPlus'
import { ulTrash01 } from '@faceup/icons/ulTrash01'
import { Button, type ButtonProps, Flex } from '@faceup/ui-base'
import { type ReactNode, useContext } from 'react'
import ReactDragListView from 'react-drag-listview'
import { twMerge } from 'tailwind-merge'
import { UiContext } from '../UiProvider/UiProvider'
import { SimpleDragListItem } from './SimpleDragListItem'

const dragListItemIdentifier = `select-form-item-option`

const defaultMinItemsCount = 0

type Item = { id: string; label: string }

export type EditableSortableListProps = {
  items: Item[]
  onMove: (fromIndex: number, toIndex: number) => void
  onAddOption: () => void
  canAddItem: boolean
  children: (data: {
    item: Item
    index: number
    config: {
      isDeletable: boolean
    }
  }) => ReactNode
  disabled?: boolean
  config?: {
    minItems?: number
    /** Once the list holds this many items, the add action is hidden, not merely disabled. */
    maxItems?: number
  }
  /**
   * Restyles the button that adds an item. `block` also widens its row and
   * pulls it up against the list, so it reads as part of it rather than as
   * something that follows it.
   */
  addButtonProps?: Pick<ButtonProps, 'type' | 'ghost' | 'block'>
  underList?: ReactNode
  additionalElement?: ReactNode
}

const EditableSortableListInner = (props: EditableSortableListProps) => {
  const {
    items,
    onMove,
    onAddOption,
    canAddItem,
    children,
    disabled = false,
    config = {
      minItems: undefined,
      maxItems: undefined,
    },
    addButtonProps = { type: 'primary', ghost: true },
    additionalElement,
    underList,
  } = props
  const { editableSortableList } = useContext(UiContext)
  const canAddMoreItems =
    canAddItem && items.length < (config?.maxItems ?? Number.POSITIVE_INFINITY)
  return (
    <>
      <input className='invisible absolute h-0 w-0' />
      <Flex vertical>
        <Flex vertical gap={8}>
          <ReactDragListView
            handleSelector={`.draggable.${dragListItemIdentifier}`}
            nodeSelector={`.${dragListItemIdentifier}-item`}
            onDragEnd={(fromIndex, toIndex) => {
              // toIndex is -1 when the item is dragged outside the list
              // Skip if dropped outside the list or at the same position
              if (toIndex < 0 || fromIndex === toIndex) {
                return
              }
              onMove(fromIndex, toIndex)
            }}
            lineClassName='drag-list-line'
          >
            <Flex vertical gap={8}>
              {items.map((item, key) => (
                <SimpleDragListItem
                  key={item.id}
                  isDraggable={!disabled}
                  identifier={dragListItemIdentifier}
                >
                  {children({
                    item,
                    index: key,
                    config: {
                      isDeletable: items.length > (config?.minItems ?? defaultMinItemsCount),
                    },
                  })}
                </SimpleDragListItem>
              ))}
            </Flex>
          </ReactDragListView>
          {underList}
        </Flex>
        {(canAddMoreItems || additionalElement) && (
          <Flex
            gap={12}
            align='center'
            // Keep in sync with the columns a row is built from: the 20px handle plus its gap in
            // `SimpleDragListItem`, and the 32px delete button plus its gap in `EditableSortableItem`.
            // Insetting by both lines the add action up with the item inputs.
            className={twMerge('mt-4', addButtonProps.block && 'ms-7 me-10 mt-2')}
          >
            {canAddMoreItems && (
              <Flex justify='left' className={twMerge(addButtonProps.block && 'min-w-0 flex-1')}>
                <Button
                  {...addButtonProps}
                  disabled={disabled}
                  onClick={onAddOption}
                  icon={<UntitledIcon icon={ulPlus} />}
                >
                  {editableSortableList?.addItem ?? 'Add option'}
                </Button>
              </Flex>
            )}
            {additionalElement}
          </Flex>
        )}
      </Flex>
    </>
  )
}

type EditableSortableItemProps = {
  children: ReactNode
  shouldDisplayDeleteButton: boolean
  deleteButtonProps: Pick<ButtonProps, 'disabled' | 'onClick'>
}

const EditableSortableItem = ({
  children,
  shouldDisplayDeleteButton,
  deleteButtonProps,
}: EditableSortableItemProps) => {
  const { editableSortableList } = useContext(UiContext)

  return (
    <Flex className='w-full'>
      <Flex align='center' gap={8} className='w-full'>
        <Flex className='w-full'>{children}</Flex>
        {shouldDisplayDeleteButton && (
          <Button
            {...deleteButtonProps}
            type='text'
            danger
            style={{ width: '32px' }}
            icon={<UntitledIcon icon={ulTrash01} />}
            label={editableSortableList?.delete ?? 'Delete'}
          />
        )}
      </Flex>
    </Flex>
  )
}

type EditableSortableListComponent = typeof EditableSortableListInner & {
  Item: typeof EditableSortableItem
}

export const EditableSortableList: EditableSortableListComponent = Object.assign(
  EditableSortableListInner,
  { Item: EditableSortableItem }
)

EditableSortableList.Item = EditableSortableItem
